import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import PriceService from "src/services/price-service"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import { columnsAtualizacaoSpreads } from "./tabela-atualizacao-spreads-constants"

const TabelaAtualizacaoSpreads = ({
    date,
    loading,
    setLoading,
    setExportFileCsv,
}) => {
    const priceService = useMemo(() => new PriceService(), [])
    const [rows, setRows] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(25)
    const [filterModel, setFilterModel] = useState({ items: [] })
    const [resetKey, setResetKey] = useState(0)

    const filterQuery = useMemo(
        () =>
            filterModel.items
                .filter((item) => item.field && item.value !== undefined && item.value !== "")
                .map(
                    (item) =>
                        `${encodeURIComponent(item.field)}=${encodeURIComponent(item.value)}`
                )
                .join("&"),
        [filterModel]
    )

    const loadSpreads = useCallback(
        async (selectedPage = 1, selectedPageSize = 25, filters = "") => {
            if (!date) return

            setLoading(true)
            try {
                const offset = (selectedPage - 1) * selectedPageSize
                const response = await priceService.getUpdateSpreads(
                    date,
                    selectedPageSize,
                    offset,
                    filters
                )
                const body = response.body || {}
                const results = body.results || body.data || []

                setRows(
                    results.map((item, index) => ({
                        ...item,
                        id: item.ID || `${item.CETIP || "item"}-${offset + index}`,
                    }))
                )
                setRowCount(body.count ?? results.length)
            } catch (error) {
                setRows([])
                setRowCount(0)
                dispatchSnackbar("Não foi possível carregar os spreads, tente novamente", "error")
            } finally {
                setLoading(false)
            }
        },
        [date, priceService, setLoading]
    )

    useEffect(() => {
        setPage(1)
        setResetKey((value) => value + 1)
        loadSpreads(1, pageSize, filterQuery)
    }, [date, filterQuery])

    useEffect(() => {
        setExportFileCsv(
            () => async () => priceService.exportUpdateSpreads(date, filterQuery)
        )
    }, [date, filterQuery, priceService, setExportFileCsv])

    const handlePageChange = (newPage, newPageSize) => {
        setPage(newPage)
        setPageSize(newPageSize)
        loadSpreads(newPage, newPageSize, filterQuery)
    }

    const handleFilterChange = (model) => {
        setFilterModel(model)
        setPage(1)
        setResetKey((value) => value + 1)
    }

    return (
        <DataGridTable
            rows={rows}
            rowPadding={2}
            columns={columnsAtualizacaoSpreads(date)}
            loading={loading}
            contained
            autoRowHeight
            disableRowSelectionOnClick
            serverSide
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            resetKey={`${resetKey}`}
            filterModel={filterModel}
            onFilterModelChange={handleFilterChange}
            onServerPageChange={handlePageChange}
        />
    )
}

TabelaAtualizacaoSpreads.propTypes = {
    date: PropTypes.string,
    loading: PropTypes.bool.isRequired,
    setLoading: PropTypes.func.isRequired,
    setExportFileCsv: PropTypes.func.isRequired,
}

export default TabelaAtualizacaoSpreads
